import React from "react";
import { Box, CardHeader, Divider, Stack, Typography } from "@mui/material";
import LeadAssignedAvatar from "./LeadAssignedAvatar";
import LeadLastMessagePreview from "./LeadLastMessagePreview";
import LeadCardHeaderActions from "./LeadCardHeaderActions";
import { Favorite as FavoriteIcon } from "@mui/icons-material";
import { formatPhone } from "../../../utils/formatters";

const AVATAR_SIZE = 56;

// Header compartilhado pelo card normal e pelo colapsado: avatar (quem atende),
// nome, telefone e as ações (temperatura/chat/menu) no CardHeader padrão do MUI
// (3 colunas: avatar/título/ação); a prévia da última mensagem fica numa linha
// própria abaixo, fora dessas 3 colunas — assim a hora sempre encosta na borda
// direita do card, e não fica limitada à largura estreita da coluna de título.
// Clicar no header abre a conversa (chat ao vivo ou prévia), igual à área
// clicável do corpo do card. Nos leads fixados, `lead.dragHandleProps` traz os
// listeners do dnd-kit: segurar o header reordena em vez de abrir.
const LeadCardHeader = ({ lead, pendingAction, isPending, fireAction, stop }) => (
  <Stack
    {...lead.dragHandleProps}
    onClick={lead.onOpenConversation}
    sx={{
      cursor: 'pointer',
      // Só nos fixados, onde o header é também a alça de arrasto:
      // - userSelect: o toque longo selecionaria o nome/telefone no caminho.
      // - WebkitTouchCallout: o iOS abre o menu de copiar/compartilhar no
      //   toque longo, que rouba o gesto antes do arrasto ativar.
      // - touchAction none: obrigatório. O sensor do dnd-kit só chama
      //   preventDefault se `event.cancelable` for true, e o browser zera esse
      //   flag assim que assume o gesto como rolagem — daí não dava mais pra
      //   cancelar. Com `manipulation` isso acontecia ao arrastar para cima
      //   (havia para onde rolar) e não para baixo (topo da lista), que é
      //   exatamente a assimetria observada.
      ...(lead.dragHandleProps
        ? {
          userSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'none',
        }
        : null),
    }}
  >
    <CardHeader
      avatar={
        <LeadAssignedAvatar
          assignedToName={lead.assignedToName}
          lastAssignedToName={lead.lastAssignedToName}
          status={lead.status}
          sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
        />
      }
      title={
        <Typography variant="h6" noWrap>
          {lead.name}{' '}
          {lead.isFavorite && (
            <FavoriteIcon fontSize="small" color="leadFavorite" sx={{ verticalAlign: 'middle' }} />
          )}
        </Typography>
      }
      subheader={
        <Typography variant="body2" color="text.secondary" noWrap>
          {formatPhone(lead.phone)}
        </Typography>
      }
      action={
        <LeadCardHeaderActions
          lead={lead}
          pendingAction={pendingAction}
          isPending={isPending}
          fireAction={fireAction}
          stop={stop}
        />
      }
      sx={{
        pb: 0, // remove o padding-bottom padrão do CardHeader (16px)
        // Sem isso, um nome grande nunca encolhe (min-width:auto padrão do
        // flex) e empurra as ações pra fora do card.
        '& .MuiCardHeader-content': { minWidth: 0 },
        // display:flex é necessário aqui: o skeleton de temperatura (quando
        // ainda não há insight) é display:block por padrão e quebraria linha
        // dentro de um container sem flex, mesmo os IconButtons sendo inline.
        '& .MuiCardHeader-action': {
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          m: 0,
        },
      }}
    />

    <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2, pb: 1.5 }}>
      {/* Espaçador do tamanho do avatar, só para alinhar visualmente com o
          nome/telefone acima — a hora usa a largura real do card, não a da coluna. */}
      <Box sx={{ width: AVATAR_SIZE, flexShrink: 0 }} />
      <LeadLastMessagePreview
        lastMessageText={lead.lastMessageText}
        lastMessageType={lead.lastMessageType}
        lastMessageDuration={lead.lastMessageDuration}
        lastMessageAt={lead.lastMessageAt}
      />
    </Stack>

    <Divider />
  </Stack>
);

export default LeadCardHeader;
